import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { RefreshCw, UserPlus, Eye, Edit, Trash2, Search, X, Phone, Mail, MapPin, Briefcase, Calendar, Award, TrendingUp, Clock } from 'lucide-react';
import { getEdamsEmployees, getWorkerProfile } from '../../../../models/externalModel';
import { getFieldWorkers, createFieldWorker, createWorkerFromEmployee, updateFieldWorker, deleteFieldWorker } from '../../../../models/mmsModel';

const WorkersList = () => {
  const [edamsEmployees, setEdamsEmployees] = useState([]);
  const [localWorkers, setLocalWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  
  const [createDialog, setCreateDialog] = useState(false);
  const [createLocalDialog, setCreateLocalDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerProfile, setWorkerProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  
  const [workerPassword, setWorkerPassword] = useState('');
  const [workerPhone, setWorkerPhone] = useState('');
  const [workerName, setWorkerName] = useState('');
  const [workerEmail, setWorkerEmail] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  const fetchEdamsEmployees = useCallback(async (searchFilter = '') => {
    setLoadingEmployees(true);
    try {
      const params = {
        limit: 1000, // Increased to show all employees (currently ~500 active)
        active: '1' // Only active employees
      };
      if (searchFilter) params.filter = searchFilter;

      const response = await getEdamsEmployees(params);
      if (response) {
        setEdamsEmployees(response.data || []);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load EDAMS employees');
      setEdamsEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  const fetchLocalWorkers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getFieldWorkers({ limit: 100 });
      console.log('Local workers response:', response);
      console.log('response.data:', response.data);
      console.log('response.data.data:', response.data?.data);
      
      // API returns: { status: 1, data: { data: [...], total: N } }
      // So response.data.data is the array of workers
      if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
        console.log('Setting workers:', response.data.data);
        setLocalWorkers(response.data.data);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback: if data is directly an array
        console.log('Setting workers (fallback):', response.data);
        setLocalWorkers(response.data);
      } else {
        console.warn('Unexpected response structure:', response);
        console.warn('Type of response.data:', typeof response.data);
        console.warn('Is response.data an array?', Array.isArray(response.data));
        setLocalWorkers([]);
      }
    } catch (err) {
      console.error('Error fetching local workers:', err);
      toast.error(err.message || 'Failed to load local workers');
      setLocalWorkers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch local workers on mount
  useEffect(() => {
    fetchLocalWorkers();
  }, [fetchLocalWorkers]);

  // Debounce search term
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Filter local workers by search term
  const filteredLocalWorkers = localWorkers.filter(worker => {
    if (!debouncedSearchTerm) return true;
    const search = debouncedSearchTerm.toLowerCase();
    return (
      worker.worker_name?.toLowerCase().includes(search) ||
      worker.worker_email?.toLowerCase().includes(search) ||
      worker.worker_phone?.includes(search) ||
      worker.worker_employee_id?.includes(search)
    );
  });

  // Filter EDAMS employees by search term (for dialog)
  const filteredEdamsEmployees = edamsEmployees.filter(emp => {
    if (!employeeSearchTerm) return true;
    const search = employeeSearchTerm.toLowerCase();
    return (
      emp.FULL_NAME?.toLowerCase().includes(search) ||
      emp.WORK_EMAIL?.toLowerCase().includes(search) ||
      emp.EMPL_NO?.includes(search)
    );
  });

  const handleCreateWorker = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }

    if (!workerPassword || workerPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    setCreating(true);
    try {
      const phoneNumber = workerPhone || selectedEmployee.WORK_TEL || selectedEmployee.PERSONAL_PHONE;
      
      await createWorkerFromEmployee(selectedEmployee.EMPL_NO, {
        password: workerPassword,
        phone: phoneNumber,
        email: `${phoneNumber}@silulumanzi.com` // Generate email from phone number
      });

      toast.success('MMS Profile created successfully');
      setCreateDialog(false);
      setSelectedEmployee(null);
      setWorkerPassword('');
      setWorkerPhone('');
      setEmployeeSearchTerm('');
      
      // Refresh local workers list
      await fetchLocalWorkers();
    } catch (err) {
      console.error('Error creating worker:', err);
      
      // Handle specific error messages
      const errorMessage = err.message || '';
      if (errorMessage.includes('UNIQUE constraint failed: tb_mms_field_workers.worker_email') || 
          errorMessage.includes('worker_email')) {
        toast.error(`An MMS Profile already exists for ${selectedEmployee.WORK_EMAIL}. This employee may already have a profile.`);
      } else if (errorMessage.includes('UNIQUE constraint') || errorMessage.includes('duplicate')) {
        toast.error('This employee already has an MMS Profile');
      } else {
        toast.error(errorMessage || 'Failed to create MMS Profile');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateDialog(true);
    setEmployeeSearchTerm('');
    setSelectedEmployee(null);
    // Load employees when dialog opens
    fetchEdamsEmployees();
  };

  const handleOpenCreateLocalDialog = () => {
    setCreateLocalDialog(true);
    setWorkerName('');
    setWorkerEmail('');
    setWorkerPhone('');
    setWorkerPassword('');
  };

  const handleCreateLocalWorker = async () => {
    if (!workerName || !workerPhone || !workerPassword) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (workerPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    setCreating(true);
    try {
      await createFieldWorker({
        name: workerName,
        phone: workerPhone,
        email: workerEmail || `${workerPhone}@silulumanzi.com`,
        password: workerPassword,
        status: '1'
      });

      toast.success('Local MMS account created successfully');
      setCreateLocalDialog(false);
      setWorkerName('');
      setWorkerEmail('');
      setWorkerPhone('');
      setWorkerPassword('');
      
      // Refresh local workers list
      await fetchLocalWorkers();
    } catch (err) {
      console.error('Error creating local worker:', err);
      toast.error(err.message || 'Failed to create local account');
    } finally {
      setCreating(false);
    }
  };

  const isWorkerCreated = (emplNo) => {
    return Array.isArray(localWorkers) && localWorkers.some(w => w.worker_employee_id === emplNo);
  };

  const handleViewWorker = async (worker) => {
    console.log('handleViewWorker called with worker:', worker);
    
    // Always open the modal with basic worker info
    setSelectedWorker(worker);
    setViewDialog(true);
    setLoadingProfile(true);
    setWorkerProfile(null);

    // Try to fetch comprehensive profile from EDAMS using worker_employee_id
    const emplNo = worker.worker_employee_id;
    console.log('Employee number (emplNo):', emplNo);
    
    if (!emplNo || emplNo === 'undefined' || emplNo === '') {
      // No valid employee number - show basic info only
      console.log('No valid employee number, showing basic info only');
      setLoadingProfile(false);
      return;
    }

    try {
      console.log(`Calling getWorkerProfile(${emplNo})...`);
      const response = await getWorkerProfile(emplNo);
      console.log('getWorkerProfile response:', response);
      
      // Check if response has data and data.success (API wraps the result)
      if (response && response.data && response.data.success) {
        console.log('Profile loaded successfully!');
        setWorkerProfile(response.data); // Use response.data instead of response
      } else {
        console.warn('Profile load failed or response.data.success is false:', response);
      }
    } catch (error) {
      console.error('Error loading worker profile:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleOpenEditDialog = (worker) => {
    setSelectedWorker(worker);
    setWorkerPhone(worker.worker_phone || '');
    setWorkerPassword('');
    setEditDialog(true);
  };

  const handleUpdateWorker = async () => {
    if (!workerPhone) {
      toast.error('Phone number is required');
      return;
    }

    if (workerPassword && workerPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    setUpdating(true);
    try {
      const updateData = {
        phone: workerPhone
      };

      // Only include password if it was changed
      if (workerPassword) {
        updateData.password = workerPassword;
      }

      await updateFieldWorker(selectedWorker.worker_id, updateData);
      
      toast.success(workerPassword ? 'Worker phone and password updated successfully' : 'Worker phone updated successfully');
      setEditDialog(false);
      setSelectedWorker(null);
      setWorkerPassword('');
      setWorkerPhone('');
      
      // Refresh workers list
      await fetchLocalWorkers();
    } catch (err) {
      console.error('Error updating worker:', err);
      toast.error(err.message || 'Failed to update worker');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Professional Header Card */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description, Results */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">MMS Artisans</h1>
                <p className="text-xs text-gray-500 mb-1">
                  Manage local artisan profiles for mobile app access
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {filteredLocalWorkers.length} {filteredLocalWorkers.length === 1 ? 'artisan' : 'artisans'}
                    {searchTerm && (
                      <span className="ml-1 text-blue-600">(filtered)</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: All Controls and Search */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              {/* Row 1: Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Create From EDAMS Button */}
                <button
                  onClick={handleOpenCreateDialog}
                  className="inline-flex items-center px-4 py-2 border border-blue-600 rounded-md shadow-sm text-sm font-medium text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  From EDAMS Employee
                </button>

                {/* Create Local Account Button */}
                <button
                  onClick={handleOpenCreateLocalDialog}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create Local Account
                </button>

                {/* Refresh Button */}
                <button
                  onClick={fetchLocalWorkers}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} style={{ color: loading ? '#0096DE' : 'currentColor' }} />
                </button>
              </div>
              
              {/* Row 2: Search Box */}
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                  placeholder="Search by name, email, phone, or employee ID..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="container mx-auto px-4">
        <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Worker ID</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Email</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Phone</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Employee No</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Status</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && (
                  <tr>
                    <td colSpan="7" className="px-3 py-8 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm text-gray-600">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
                
                {!loading && filteredLocalWorkers.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-3 py-8 text-center">
                      <p className="text-gray-600">
                        {searchTerm ? 'No artisans found matching your search' : 'No MMS profiles created yet'}
                      </p>
                      {!searchTerm && (
                        <button
                          onClick={handleOpenCreateDialog}
                          className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Create your first MMS Profile
                        </button>
                      )}
                    </td>
                  </tr>
                )}

                {!loading && filteredLocalWorkers.map(worker => (
                  <tr key={worker.worker_id} className="hover:bg-blue-50 transition-colors">
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{worker.worker_id}</td>
                    <td className="px-3 py-2 text-sm text-gray-700">{worker.worker_name}</td>
                    <td className="px-3 py-2 text-sm text-gray-700">{worker.worker_email || 'N/A'}</td>
                    <td className="px-3 py-2 text-sm text-gray-700">{worker.worker_phone}</td>
                    <td className="px-3 py-2 text-sm text-gray-700">{worker.worker_employee_id || 'N/A'}</td>
                    <td className="px-3 py-2 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${
                        worker.worker_status === '1' ? 'bg-green-100 text-green-800 border-green-300' : 'bg-gray-100 text-gray-800 border-gray-300'
                      }`}>
                        {worker.worker_status === '1' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleViewWorker(worker)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="View Profile"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleOpenEditDialog(worker)}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                          title="Edit Phone/Password"
                        >
                          <Edit size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Local Account Dialog */}
      {createLocalDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Dialog Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Create Local MMS Account</h2>
              <p className="text-sm text-gray-600 mt-1">Create an account without EDAMS employee linking (for testing)</p>
            </div>

            {/* Dialog Body */}
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={workerName}
                  onChange={(e) => setWorkerName(e.target.value)}
                  placeholder="Enter full name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={workerPhone}
                  onChange={(e) => setWorkerPhone(e.target.value)}
                  placeholder="Enter phone number (e.g., 0821234567)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">Will be used for login</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="email"
                  value={workerEmail}
                  onChange={(e) => setWorkerEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">Can also be used for login</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={workerPassword}
                  onChange={(e) => setWorkerPassword(e.target.value)}
                  placeholder="Enter password (min 4 characters)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">For mobile/web app login</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> This creates a standalone account for testing purposes. 
                  It won't be linked to an EDAMS employee record.
                </p>
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setCreateLocalDialog(false);
                  setWorkerName('');
                  setWorkerEmail('');
                  setWorkerPhone('');
                  setWorkerPassword('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateLocalWorker}
                disabled={!workerName || !workerPhone || !workerPassword || creating}
                className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
              >
                {creating ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <UserPlus size={16} />
                    <span>Create Account</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create MMS Profile Dialog */}
      {createDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Dialog Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Create MMS Profile</h2>
              <p className="text-sm text-gray-600 mt-1">Select an EDAMS employee to create their MMS artisan profile</p>
            </div>

            {/* Dialog Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Form Fields - Always visible at top */}
              <div className="mb-4 grid grid-cols-2 gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={workerPhone}
                    onChange={(e) => setWorkerPhone(e.target.value)}
                    placeholder="Enter phone number"
                    disabled={!selectedEmployee}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={workerPassword}
                    onChange={(e) => setWorkerPassword(e.target.value)}
                    placeholder="Enter password for artisan"
                    disabled={!selectedEmployee}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">For mobile app login</p>
                </div>
              </div>

              {/* Search Box */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={employeeSearchTerm}
                    onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                    placeholder="Search employees by name, email, or employee number..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Showing {filteredEdamsEmployees.length} of {edamsEmployees.length} employees
                </p>
              </div>

              {/* Selected Employee Display */}
              {selectedEmployee && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{selectedEmployee.FULL_NAME}</h3>
                      <div className="text-sm text-gray-600 mt-1 space-y-1">
                        <p><span className="font-medium">Employee No:</span> {selectedEmployee.EMPL_NO}</p>
                        <p><span className="font-medium">Email:</span> {selectedEmployee.WORK_EMAIL || 'N/A'}</p>
                        <p><span className="font-medium">Phone:</span> {selectedEmployee.WORK_TEL || selectedEmployee.PERSONAL_PHONE || 'N/A'}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedEmployee(null)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Clear selection"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>
              )}

              {/* Employee List */}
              {loadingEmployees ? (
                <div className="flex items-center justify-center py-12">
                  <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : filteredEdamsEmployees.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">No employees found matching your search</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    {filteredEdamsEmployees.map((emp) => {
                      const isCreated = isWorkerCreated(emp.EMPL_NO);
                      const isSelected = selectedEmployee?.EMPL_NO === emp.EMPL_NO;
                      
                      return (
                        <button
                          key={emp.EMPL_NO}
                          onClick={() => {
                            if (!isCreated) {
                              setSelectedEmployee(emp);
                              setWorkerPhone(emp.WORK_TEL || emp.PERSONAL_PHONE || '');
                            }
                          }}
                          disabled={isCreated}
                          className={`w-full text-left px-4 py-3 border-b border-gray-200 last:border-b-0 transition-colors ${
                            isCreated
                              ? 'bg-gray-50 cursor-not-allowed opacity-60'
                              : isSelected
                              ? 'bg-blue-100 border-blue-300'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900">{emp.FULL_NAME}</h4>
                                {isCreated && (
                                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-300">
                                    Already Created
                                  </span>
                                )}
                                {emp.ACTIVE !== '1' && (
                                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-300">
                                    Inactive
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 mt-1 space-y-0.5">
                                <p><span className="font-medium">Employee No:</span> {emp.EMPL_NO}</p>
                                <p><span className="font-medium">Email:</span> {emp.WORK_EMAIL || 'N/A'}</p>
                                <p><span className="font-medium">Phone:</span> {emp.WORK_TEL || emp.PERSONAL_PHONE || 'N/A'}</p>
                              </div>
                            </div>
                            {isSelected && !isCreated && (
                              <div className="ml-4">
                                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Dialog Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setCreateDialog(false);
                  setSelectedEmployee(null);
                  setWorkerPhone('');
                  setWorkerPassword('');
                  setEmployeeSearchTerm('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWorker}
                disabled={!selectedEmployee || !workerPhone || !workerPassword || creating}
                className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
              >
                {creating ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <UserPlus size={16} />
                    <span>Create Profile</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Worker Dialog */}
      {viewDialog && selectedWorker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
            {/* Dialog Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Worker Profile</h2>
                <button
                  onClick={() => {
                    setViewDialog(false);
                    setSelectedWorker(null);
                    setWorkerProfile(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Dialog Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingProfile ? (
                <div className="flex items-center justify-center py-12">
                  <svg className="animate-spin h-12 w-12 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Basic Info Card */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {workerProfile?.profile?.FULL_NAME || selectedWorker.worker_name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                          {workerProfile?.profile?.POSITION_NAME || 'Artisan'}
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex items-center text-sm text-gray-700">
                            <Mail className="w-4 h-4 mr-2 text-blue-600" />
                            <span>{selectedWorker.worker_email || 'N/A'}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-700">
                            <Phone className="w-4 h-4 mr-2 text-blue-600" />
                            <span>{selectedWorker.worker_phone || 'N/A'}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-700">
                            <MapPin className="w-4 h-4 mr-2 text-blue-600" />
                            <span>{workerProfile?.profile?.STATION_NAME || 'N/A'}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-700">
                            <Briefcase className="w-4 h-4 mr-2 text-blue-600" />
                            <span>Employee No: {selectedWorker.worker_employee_id || 'N/A'}</span>
                          </div>
                          {workerProfile?.profile?.TEAM_NAME && (
                            <div className="flex items-center text-sm text-gray-700">
                              <Award className="w-4 h-4 mr-2 text-blue-600" />
                              <span>{workerProfile.profile.TEAM_NAME}</span>
                            </div>
                          )}
                          {workerProfile?.profile?.EMPLOY_START && (
                            <div className="flex items-center text-sm text-gray-700">
                              <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                              <span>Since {new Date(workerProfile.profile.EMPLOY_START).getFullYear()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                          selectedWorker.worker_status === '1' 
                            ? 'bg-green-100 text-green-800 border border-green-300' 
                            : 'bg-gray-100 text-gray-800 border border-gray-300'
                        }`}>
                          {selectedWorker.worker_status === '1' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Statistics Grid */}
                  {workerProfile?.statistics && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Total Jobs</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">
                              {workerProfile.statistics.total_jobs || 0}
                            </p>
                          </div>
                          <Briefcase className="w-8 h-8 text-blue-500 opacity-50" />
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Completed</p>
                            <p className="text-2xl font-bold text-green-600 mt-1">
                              {workerProfile.statistics.completed_jobs || 0}
                            </p>
                          </div>
                          <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Pending</p>
                            <p className="text-2xl font-bold text-amber-600 mt-1">
                              {workerProfile.statistics.pending_jobs || 0}
                            </p>
                          </div>
                          <Clock className="w-8 h-8 text-amber-500 opacity-50" />
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Avg Hours</p>
                            <p className="text-2xl font-bold text-purple-600 mt-1">
                              {workerProfile.statistics.avg_completion_hours 
                                ? parseFloat(workerProfile.statistics.avg_completion_hours).toFixed(1)
                                : '0'}
                            </p>
                          </div>
                          <Clock className="w-8 h-8 text-purple-500 opacity-50" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Top Skills */}
                  {workerProfile?.top_skills && workerProfile.top_skills.length > 0 && (
                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Award className="w-5 h-5 mr-2 text-blue-600" />
                        Top Skills & Expertise
                      </h4>
                      <div className="space-y-4">
                        {workerProfile.top_skills.map((skill, idx) => (
                          <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <h5 className="font-semibold text-gray-900">{skill.ACTIVITY_NAME}</h5>
                                <p className="text-sm text-gray-600">{skill.ACTIVITY_DESCRIPTION}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-blue-600">
                                  {parseFloat(skill.completion_rate || 0).toFixed(0)}%
                                </p>
                                <p className="text-xs text-gray-500">completion rate</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">
                                {skill.jobs_handled} jobs handled
                              </span>
                              <span className="text-green-600 font-medium">
                                {skill.completed} completed
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No EDAMS data message */}
                  {!workerProfile && !loadingProfile && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                      <p className="text-amber-800">
                        Extended profile data not available. Worker may not be linked to EDAMS employee record.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Dialog Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => handleOpenEditDialog(selectedWorker)}
                className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
              >
                <Edit size={16} />
                <span>Edit Phone/Password</span>
              </button>
              <button
                onClick={() => {
                  setViewDialog(false);
                  setSelectedWorker(null);
                  setWorkerProfile(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Worker Dialog */}
      {editDialog && selectedWorker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Dialog Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Edit Worker</h2>
              <p className="text-sm text-gray-600 mt-1">{selectedWorker.worker_name}</p>
            </div>

            {/* Dialog Body */}
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={workerPhone}
                  onChange={(e) => setWorkerPhone(e.target.value)}
                  placeholder="Enter phone number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="password"
                  value={workerPassword}
                  onChange={(e) => setWorkerPassword(e.target.value)}
                  placeholder="Leave empty to keep current password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Only fill this if you want to change the password</p>
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setEditDialog(false);
                  setSelectedWorker(null);
                  setWorkerPassword('');
                  setWorkerPhone('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateWorker}
                disabled={!workerPhone || updating}
                className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
              >
                {updating ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Updating...</span>
                  </>
                ) : (
                  <span>Update Worker</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkersList;
